import {
  IRecord_Project,
} from '../types/schema/project'

import {
  IRecord_Marketing_Campaign_Data,
  IRecord_Marketing_Post_Core,
  IData_Marketing_Post_Schedule,
  IData_Marketing_Post_Time,
  IData_Marketing_Project_Settings,
  IData_Marketing_Post_Setup,
  IData_Post_State,
  IRecord_Marketing_Campaign,
} from '../types/schema/marketing'

import {
  ITree_Org,
  INode_Org,
} from '../types/schema/tree'

import {
  findParentOrg,
} from '../utils/tree'

import {
  IMeta_OrgAppData_Marketing,
} from '../types/schema/app_config'

import {
  IMeta_ProjectConfig_App_Config_Marketing,
} from '../types/schema/project_config'

import {
  MARKETING_POST_TYPE_ORIGINAL,
  MARKETING_POST_TYPE_LOCALISED,
  MARKETING_POST_STATE_SCHEDULED,
  MARKETING_DAY_OF_WEEK_TITLES,
  MARKETING_MONTH_TITLES,
  MARKETING_POST_STATE_GENERATED,
  MARKETING_POST_STATE_APPROVED,
  MARKETING_POST_STATE_LOCALISED,
  MARKETING_POST_STATE_ERROR,
  MARKETING_POST_STATE_GENERATING,
  MARKETING_POST_STATE_LOCALISING,
  MARKETING_POST_STATES,
  MARKETING_CAMPAIGN_MODE_NETWORK,
} from '../constants'

export const getProjectConfig = (
  project: IRecord_Project,
): IMeta_ProjectConfig_App_Config_Marketing => {
  if(!project.meta || !project.meta.apps) throw new Error(`no project meta found`)
  const meta = project.meta
  const app = meta.apps.find(app => app.namespace == 'marketing')
  if(!app || !app.config) throw new Error(`marketing config not found`)
  return app.config as IMeta_ProjectConfig_App_Config_Marketing
}

export const isRootOrg = (
  org: INode_Org,
) => {
  if(!org) return false
  return org.meta?.appconfig?.marketing?.is_root_org ? true : false
}

export const findRootOrg = (
  orgTree: ITree_Org,
  orgId: string,
) => findParentOrg(
  orgTree,
  orgId,
  isRootOrg,
  true,
)

// extra the org level marketing config object
// IMeta_OrgAppData_Marketing
export const getOrgAppData = (
  orgTree: ITree_Org,
  orgId: string,
): IMeta_OrgAppData_Marketing => {
  const org = orgTree.info.mapId[orgId]
  if(!org) throw new Error(`org not found`)
  return org.meta?.appdata?.marketing || {}
}

export const getGodContentSchema = (config: IMeta_ProjectConfig_App_Config_Marketing) => {
  return config.god_content_schema || []
}

export const getUnlockedGodContentFields = (config: IMeta_ProjectConfig_App_Config_Marketing) => {
  return getGodContentSchema(config)
    .filter(field => field.meta?.locked ? false : true)
    .map(field => field.id)
}

export const getHiddenGodContentFields = (config: IMeta_ProjectConfig_App_Config_Marketing) => {
  return getGodContentSchema(config)
    .filter(field => field.meta?.hidden ? false : true)
    .map(field => field.id)
}

export const getOrgGodContent = (
  orgTree: ITree_Org,
  orgId: string,
): Record<string, string> => {
  const appData = getOrgAppData(orgTree, orgId)
  return appData.god_content || {}
}

// merge the god content values from the root org to the current org
// allow only the "locked": false fields to be overridden by the franchisee
export const getGodContentValues = (
  project: IRecord_Project,
  orgTree: ITree_Org,
  orgId: string,
): Record<string, any> => {
  const projectConfig = getProjectConfig(project)
  const rootOrg = findRootOrg(orgTree, orgId)
  if(!rootOrg) throw new Error(`no root org found`)
  if(rootOrg.id == orgId) {
    return getOrgGodContent(orgTree, orgId)
  }
  const childOrg = orgTree.info.mapId[orgId]
  if(!childOrg) throw new Error(`org not found`)
  const rootGodContent = getOrgGodContent(orgTree, rootOrg.id)
  const childGodContent = getOrgGodContent(orgTree, childOrg.id)
  const unlockedValues = getUnlockedGodContentFields(projectConfig)
    .reduce((all, field) => {
      all[field] = childGodContent[field] || rootGodContent[field]
      return all
    }, {})
  return Object.assign({}, rootGodContent, unlockedValues)
}

export const padNumber = (n: number, min = 2): string => {
  let ret = n.toString()
  while(ret.length < min) {
    ret = '0' + ret
  }
  return ret
}

export const processDate = (dt: Date | string): Date => typeof(dt) == 'string' ? new Date(dt) : dt

export const getDateString = (dt: Date | string, utc = true): string => {
  dt = processDate(dt)
  const year = utc ? dt.getUTCFullYear() : dt.getFullYear()
  const month = (utc ? dt.getUTCMonth() : dt.getMonth()) + 1
  const day = utc ? dt.getUTCDate() : dt.getDate()
  return `${year}-${padNumber(month)}-${padNumber(day)}`
}

export const getTimeString = (dt: Date | string, utc = true): string => {
  dt = processDate(dt)
  const hours = utc ? dt.getUTCHours() : dt.getHours()
  const minutes = utc ? dt.getUTCMinutes() : dt.getMinutes()
  const seconds = utc ? dt.getUTCSeconds() : dt.getSeconds()
  return `${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}`
}

export const getFullDateString = (dt: Date | string): string => {
  dt = processDate(dt)
  return `${getDateString(dt)}T${getTimeString(dt)}Z`
}

export const getTimeTitleFromDate = (dt: Date | string): string => {
  dt = processDate(dt)
  return `${padNumber(dt.getHours())}:${padNumber(dt.getMinutes())}`
}

export const getDateTimeTitleFromDate = (dt: Date | string): string => {
  dt = processDate(dt)
  return dt.toLocaleString()
}

export const getDayOfWeekTitle = (day_of_week: number): string => MARKETING_DAY_OF_WEEK_TITLES[day_of_week]
export const getTimeTitle = (time: IData_Marketing_Post_Time): string => {
  return `${padNumber(time.hour)}:${padNumber(time.minute)}`
}

export const getDefaultCampaignFormData = (data: Partial<IRecord_Marketing_Campaign_Data> = {}): IRecord_Marketing_Campaign_Data => {
  const start_date = new Date()

  // start the campaign tomorrow
  start_date.setTime(start_date.getTime() + (1000 * 60 * 60 * 24))

  // make it last 30 days by default
  const end_date = new Date()
  end_date.setTime(start_date.getTime() + (1000 * 60 * 60 * 24 * 30))
  
  const values = Object.assign(
    {},
    {
      name: data.name,
      color: data.color,
      mode: data.mode || MARKETING_CAMPAIGN_MODE_NETWORK,
      approval_days: data.approval_days || 30,
      start_date: data.start_date || getDateString(start_date),
      end_date: data.end_date || getDateString(end_date),
      posts: [],
      themes: [],
      form_data: {},
      example_theme: '',
      example_localise_id: '',
      example_posts: {},
    },
    data.form_data,
  )
  return values
}

export const DAY = (1000 * 60 * 60 * 24)
export const getMonthTitle = (month: number): string => MARKETING_MONTH_TITLES[month]
export const getMonthNumber = (dt: Date | string) => {
  dt = processDate(dt)
  return (dt.getFullYear() * 100) + dt.getMonth()
}
export const getDayNumber = (dt: Date | string) => {
  dt = processDate(dt)
  return Math.floor(dt.getTime() / DAY)
}
export const fromDayNumber = (days: number) => {
  return new Date(days * DAY)
}
export const increaseDayNumber = (dt: Date | string, amount = 1) => {
  dt = processDate(dt)
  const newDayDate = fromDayNumber(getDayNumber(dt) + amount)
  newDayDate.setUTCHours(dt.getUTCHours())
  newDayDate.setUTCMinutes(dt.getUTCMinutes())
  newDayDate.setUTCSeconds(dt.getUTCSeconds())
  return newDayDate
}

export const isSameDay = (dt1: Date | string, dt2: Date | string): boolean => {
  dt1 = processDate(dt1)
  dt2 = processDate(dt2)
  return getDateString(dt1) == getDateString(dt2)
}

export const isSameTime = (dt1: Date | string, dt2: Date | string): boolean => {
  dt1 = processDate(dt1)
  dt2 = processDate(dt2)
  return getTimeString(dt1) == getTimeString(dt2)
}

export const isBeforeNow = (dt: Date | string): boolean => {
  return processDate(dt).getTime() < new Date().getTime()
}

export const copyTimeFromDate = (dt: Date | string, copyFrom: Date | string): Date => {
  const newDate = new Date(processDate(dt).getTime())
  copyFrom = processDate(copyFrom)
  newDate.setUTCHours(copyFrom.getUTCHours())
  newDate.setUTCMinutes(copyFrom.getUTCMinutes())
  newDate.setUTCSeconds(copyFrom.getUTCSeconds())
  return newDate
}

export const CampaignModel = (
  data: IRecord_Marketing_Campaign_Data,
  config: IData_Marketing_Project_Settings,
) => {
  const getMonths = (): [number, number][] => {
    const startDate = new Date(data.start_date)
    const endDate = new Date(data.end_date)
    const currentDate = new Date(startDate)

    let ret: [number, number][] = []

    while(getMonthNumber(currentDate) <= getMonthNumber(endDate)) {
      ret.push([
        currentDate.getFullYear(),
        currentDate.getMonth(),
      ])
      if(currentDate.getMonth() == 11) {
        currentDate.setMonth(0)
        currentDate.setFullYear(currentDate.getFullYear() + 1)
      } else {
        currentDate.setMonth(currentDate.getMonth() + 1)
      }
    }
    return ret
  }

  const getTemplate = (templateID: string) => config.post_templates.find((template) => template.id == templateID)
  const getSchedule = (scheduleID: string) => config.post_schedules.find((schedule) => schedule.id == scheduleID)
  const getPostSetup = (templateID: string): IData_Marketing_Post_Setup => {
    const matchingPost = data.posts.find(post => post.template == templateID)
    if(!matchingPost) throw new Error(`no post found for id: ${templateID}`)
    const template = getTemplate(matchingPost.template)
    if(!template) throw new Error(`no template found for id: ${matchingPost.template}`)
    const schedule = getSchedule(matchingPost.schedule)
    if(!schedule) throw new Error(`no schedule found for id: ${matchingPost.schedule}`)
    return {
      template,
      schedule,
    }
  }

  const getPostSetups = () => data.posts.map(post => getPostSetup(post.template))

  // get all posts expanded for a single campaign post template
  // this means exploding the post schedule into a list of posts
  const getTemplatePosts = (campaignID: string, templateID: string) => {
    if(!templateID) throw new Error(`no templateID given`)
    const posts: IRecord_Marketing_Post_Core[] = []
  
    const postSetup = getPostSetup(templateID)
    const currentDate = new Date(data.start_date)
    const endDate = new Date(data.end_date)

    if(getDayNumber(endDate) < getDayNumber(currentDate)) throw new Error(`end date cannot be before start date`)

    const postDates = explodeSchedule(postSetup.schedule, new Date(data.start_date), new Date(data.end_date))
    postDates.forEach(date => {
      const generateDate = increaseDayNumber(date, -data.approval_days)
      posts.push({
        marketing_campaign: campaignID,
        state: MARKETING_POST_STATE_SCHEDULED,
        template_id: templateID,
        network_id: postSetup.template.network,
        post_date: date.toISOString(),
        generate_date: generateDate.toISOString(),
        type: MARKETING_POST_TYPE_ORIGINAL,
        data: {
          history: [],  
        }
      })
    })

    return posts
  }

  const getAllPosts = (campaignID: string) => {
    let postsToCreate: IRecord_Marketing_Post_Core[] = []
    data.posts.forEach(post => {
      postsToCreate = postsToCreate.concat(getTemplatePosts(campaignID, post.template))
    })
    return postsToCreate
  }

  // we know the date of the post and we know the list
  // of the themes (one per month)
  // so let's return the actual theme this post should use
  const getPostTheme = (
    post_date: Date,
  ) => {
    const months = getMonths()
    const themes = data.themes || []
    const matchingMonthIndex = months.findIndex(([year, month]) => {
      return post_date.getFullYear() == year && post_date.getMonth() == month
    })
    if(matchingMonthIndex < 0) throw new Error(`no theme found for post date ${post_date}`)
    // if the index is more than the themes then throw
    if(matchingMonthIndex >= themes.length) throw new Error(`no theme found for post date ${post_date}`)
    return themes[matchingMonthIndex]
  }

  return {
    getMonths,
    getTemplatePosts,
    getTemplate,
    getSchedule,
    getPostSetup,
    getPostSetups,
    getAllPosts,
    getPostTheme,
  }
}

export function explodeSchedule(
  schedule: IData_Marketing_Post_Schedule,
  startDate: Date,
  endDate: Date
): Date[] {
  const result: Date[] = []
  const timesWithoutDay = schedule.times.filter((time) => time.day_of_week === undefined)
  const timesWithDay = schedule.times.filter((time) => time.day_of_week !== undefined)
  for (
    let currentDate = new Date(startDate.getTime());
    getDayNumber(currentDate) <= getDayNumber(endDate);
    currentDate = increaseDayNumber(currentDate)
  ) {
    // check if the day of the week matches
    if (schedule.days_of_week.indexOf(currentDate.getDay()) >= 0) {
      timesWithoutDay.forEach((time) => {
        const nextDate = fromDayNumber(getDayNumber(currentDate))
        nextDate.setHours(time.hour)
        nextDate.setMinutes(time.minute)
        result.push(nextDate)
      })
    }
    
    timesWithDay.forEach((time) => {
      if(time.day_of_week === currentDate.getDay()) {
        const nextDate = fromDayNumber(getDayNumber(currentDate))
        nextDate.setHours(time.hour)
        nextDate.setMinutes(time.minute)
        result.push(nextDate)
      }
    })
  }
  return result
}

export const hasSeenState = (checkState: IData_Post_State, pastState: IData_Post_State): boolean => {
  const checkIndex = MARKETING_POST_STATES.indexOf(checkState)
  const pastIndex = MARKETING_POST_STATES.indexOf(pastState)
  return checkIndex >= pastIndex
}

export const isPastState = (checkState: IData_Post_State, pastState: IData_Post_State): boolean => {
  const checkIndex = MARKETING_POST_STATES.indexOf(checkState)
  const pastIndex = MARKETING_POST_STATES.indexOf(pastState)
  return checkIndex > pastIndex
}

export const isBeforeState = (checkState: IData_Post_State, futureState: IData_Post_State): boolean => {
  const checkIndex = MARKETING_POST_STATES.indexOf(checkState)
  const futureIndex = MARKETING_POST_STATES.indexOf(futureState)
  return checkIndex <= futureIndex
}

export const isErrorState = (state: IData_Post_State) => state == MARKETING_POST_STATE_ERROR
export const isErrorGeneratingState = (state: IData_Post_State, error_state: IData_Post_State) => {
  if(state != MARKETING_POST_STATE_ERROR) return false
  return error_state == MARKETING_POST_STATE_GENERATING || error_state == MARKETING_POST_STATE_LOCALISING
}
export const hasPostBeenGenerated = (state: IData_Post_State) => hasSeenState(state, MARKETING_POST_STATE_GENERATED)
export const hasPostBeenApproved = (state: IData_Post_State) => hasSeenState(state, MARKETING_POST_STATE_APPROVED)
export const isPostGenerating = (state: IData_Post_State) => isBeforeState(state, MARKETING_POST_STATE_GENERATED)
export const doesPostNeedModerating = (state: IData_Post_State) => state == MARKETING_POST_STATE_GENERATED

export const getContentGenerationDate = (post: IRecord_Marketing_Post_Core, campaign: IRecord_Marketing_Campaign): Date => {
  const postDate = new Date(post.post_date)
  postDate.setTime(postDate.getTime() - (campaign.data.approval_days * 24 * 60 * 60 * 1000))
  return postDate
}

// this must be a post that is part of a campaign that is network mode
// it must have just been moved to approved from generated
// and it must be an original type post
export const shouldLocalisePost = (
  campaign: IRecord_Marketing_Campaign,
  post: IRecord_Marketing_Post_Core,
  previousState: IData_Post_State,
): boolean => {
  if(campaign.data.mode != MARKETING_CAMPAIGN_MODE_NETWORK) return false
  if(post.type != MARKETING_POST_TYPE_ORIGINAL) return false
  if(post.state != MARKETING_POST_STATE_APPROVED) return false
  if(previousState != MARKETING_POST_STATE_GENERATED) return false
  return true
}

export const hasPostLocalised = (post: IRecord_Marketing_Post_Core): boolean => {
  return post.type == MARKETING_POST_TYPE_ORIGINAL && hasSeenState(post.state, MARKETING_POST_STATE_LOCALISED)
}

export const isPostWaitingToBeLocalised = (post: IRecord_Marketing_Post_Core): boolean => {
  return post.type == MARKETING_POST_TYPE_ORIGINAL && post.state == MARKETING_POST_STATE_GENERATED
}

// TODO: this is a fucking mess (blame Kai)
export const isLocalPostWaitingForModeration = (post: IRecord_Marketing_Post_Core): boolean => {
  return post.type == MARKETING_POST_TYPE_LOCALISED && post.state == MARKETING_POST_STATE_GENERATED
}
